import { gql } from '@apollo/client';

export const GET_LISTING_SLUGS = gql`
  query GetListingPageSlugs(
    $tag: String!
    $locale: String!
    $preview: Boolean
  ) {
    meetingPlacePageEventListCollection(
      where: { contentfulMetadata: { tags: { id_contains_some: [$tag] } } }
      limit: 1
      preview: $preview
    ) {
      items {
        linkedFrom {
          libraryMenuGroupCollection(limit: 10) {
            items {
              pagesCollection(locale: $locale) {
                items {
                  __typename
                  ... on MeetingPlacePageEventList {
                    sys {
                      id
                    }
                    slug
                  }
                }
              }
            }
          }
        }
      }
    }

    meetingPlacePageOccasionsCollection(
      where: { contentfulMetadata: { tags: { id_contains_some: [$tag] } } }
      limit: 1
    ) {
      items {
        linkedFrom {
          libraryMenuGroupCollection(limit: 10) {
            items {
              pagesCollection(locale: $locale) {
                items {
                  __typename
                  ... on MeetingPlacePageOccasions {
                    sys {
                      id
                    }
                    slug
                  }
                }
              }
            }
          }
        }
      }
    }

    meetingPlacePageOfferListCollection(
      where: { contentfulMetadata: { tags: { id_contains_some: [$tag] } } }
      limit: 1
    ) {
      items {
        linkedFrom {
          libraryMenuGroupCollection(limit: 10) {
            items {
              pagesCollection(locale: $locale) {
                items {
                  __typename
                  ... on MeetingPlacePageOfferList {
                    sys {
                      id
                    }
                    slug
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export interface ListingSlugs {
  eventListPageSlug: string | null;
  offerListPageSlug: string | null;
}

const extractSlug = (collectionData: any, typename: string): string | null => {
  const pages =
    collectionData?.items?.[0]?.linkedFrom?.libraryMenuGroupCollection?.items
      ?.flatMap((group: any) => group?.pagesCollection?.items ?? [])
      ?.filter((item: any) => item?.__typename === typename) ?? [];
  return pages?.[0]?.slug ?? null;
};

/**
 * Fetches event list, occasions and offer list slugs from Contentful.
 * Uses a single query for all three.
 *
 * @example
 * // Get all slugs
 * const { eventListPageSlug, offerListPageSlug } =
 *   await getListingPageSlugs(client, tag, locale);
 *
 * // Get event slug (falls back to occasions if eventList not found) as for offers
 * const { eventListPageSlug } = await getListingPageSlugs(client, tag, locale);
 */
export const getListingPageSlugs = async (
  client: any,
  tag: string,
  locale: string,
  preview?: boolean,
): Promise<ListingSlugs> => {
  try {
    const result = await client.query({
      query: GET_LISTING_SLUGS,
      variables: { tag, locale, preview },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

    const data = result?.data;

    return {
      // Falls back to occasions if eventList slug not found
      // matches your existing fallback logic
      eventListPageSlug:
        extractSlug(
          data?.meetingPlacePageEventListCollection,
          'MeetingPlacePageEventList',
        ) ??
        extractSlug(
          data?.meetingPlacePageOccasionsCollection,
          'MeetingPlacePageOccasions',
        ),

      offerListPageSlug:
        extractSlug(
          data?.meetingPlacePageOfferListCollection,
          'MeetingPlacePageOfferList',
        ) ??
        extractSlug(
          data?.meetingPlacePageOccasionsCollection,
          'MeetingPlacePageOccasions',
        ),
    };
  } catch (error) {
    console.error('[getListingPageSlugs] Error:', error);
    return {
      eventListPageSlug: null,
      offerListPageSlug: null,
    };
  }
};
